import { createRouter, createWebHistory, type RouteLocationNormalized, type RouteRecordRaw } from 'vue-router'

import { useAuthStore } from '@/features/auth/authStore'
import { hasCapability, type Capability } from '@/features/auth/capabilities'
import { clientConfig } from '@/app/branding'
import i18n from '@/i18n'
import type { AuthView, DashboardPageFamily } from '@/dashboard/themeContract'

const AuthLayout = () => import('@/layouts/AuthLayout.vue')
const DashboardLayout = () => import('@/layouts/DashboardLayout.vue')

const authenticated = (capability: Capability, title: string, pageFamily: DashboardPageFamily, layout = 'dashboard') => ({
  auth: true,
  requiresAuth: true,
  capability,
  title,
  pageFamily,
  layout,
})

const authentication = (title: string, authView: AuthView) => ({ title, layout: 'auth', authView })

export const routes: RouteRecordRaw[] = [
  { path: '/', redirect: { name: 'login' } },
  {
    path: '/',
    component: DashboardLayout,
    children: [
      {
        name: 'ui-kit',
        path: 'ui-kit',
        meta: authenticated('viewAccount', 'interface.ui_kit', 'system'),
        component: () => import('@/dashboard/themes/theme-6/UIKit.vue'),
      },
      {
        name: 'home',
        path: 'dashboard',
        meta: authenticated('viewDashboard', 'menu.dashboard', 'overview'),
        component: () => import('@/dashboard/themes/theme-6/Home.vue'),
      },
      {
        name: 'generate-proxies',
        path: 'generate-proxies',
        meta: authenticated('viewDashboard', 'interface.generate_proxies', 'operations'),
        component: () => import('@/dashboard/themes/theme-6/GenerateProxies.vue'),
      },
      {
        name: 'account',
        path: 'account',
        meta: authenticated('viewAccount', 'menu.account', 'account'),
        component: () => import('@/pages/dashboard/Account.vue'),
      },
      {
        name: 'checkout',
        path: 'checkout',
        meta: authenticated('buyProxies', 'interface.checkout', 'commerce', 'checkout'),
        component: () => import('@/pages/dashboard/Checkout.vue'),
      },
      {
        name: 'inventory',
        path: 'inventory',
        meta: authenticated('viewDashboard', 'menu.inventory', 'commerce'),
        component: () => import('@/pages/dashboard/Inventory.vue'),
      },
      {
        name: 'buy-proxies',
        path: 'buy-proxies',
        meta: authenticated('viewInvoices', 'menu.buy_proxies', 'commerce'),
        component: () => import('@/dashboard/themes/theme-6/BuyProxies.vue'),
      },
      {
        name: 'invoices',
        path: 'invoices',
        meta: authenticated('viewInvoices', 'common.invoices', 'commerce', 'invoices'),
        redirect: (to) => ({ name: 'buy-proxies', query: to.query, hash: '#invoices' }),
      },
      {
        name: 'reseller',
        path: 'users',
        meta: authenticated('manageUsers', 'common.users', 'operations', 'reseller'),
        component: () => import('@/dashboard/themes/theme-6/Users.vue'),
      },
      {
        name: 'user-workspace',
        path: 'users/:id',
        meta: authenticated('manageUsers', 'common.users', 'operations', 'reseller'),
        component: () => import('@/dashboard/themes/theme-6/UserWorkspace.vue'),
      },
      {
        name: 'referrals',
        path: 'referrals',
        meta: authenticated('viewReferrals', 'menu.referrals', 'marketing'),
        component: () => import('@/pages/dashboard/Referrals.vue'),
      },
      {
        name: 'marketer',
        path: 'marketer',
        meta: authenticated('viewMarketing', 'interface.marketing', 'marketing'),
        component: () => import('@/pages/dashboard/Marketer.vue'),
      },
      {
        name: 'news',
        path: 'news',
        meta: authenticated('viewNews', 'common.news', 'operations'),
        component: () => import('@/pages/dashboard/News.vue'),
      },
      {
        name: 'wallet',
        path: 'wallet',
        meta: authenticated('viewInvoices', 'menu.wallet', 'commerce'),
        component: () => import('@/pages/dashboard/Wallet.vue'),
      },
      {
        name: 'support-contact',
        path: 'support-contact',
        meta: authenticated('viewSupport', 'interface.contact_support', 'support'),
        component: () => import('@/pages/dashboard/SupportContact.vue'),
      },
      {
        name: 'support-tos',
        path: 'support-tos',
        meta: authenticated('viewSupport', 'menu.tos', 'legal'),
        component: () => import('@/pages/dashboard/SupportTOS.vue'),
      },
      {
        name: 'support-policy',
        path: 'support-policy',
        meta: authenticated('viewSupport', 'menu.policy', 'legal'),
        component: () => import('@/pages/dashboard/SupportPolicy.vue'),
      },
    ],
  },
  {
    path: '/',
    component: AuthLayout,
    children: [
      {
        name: 'login',
        path: 'login',
        meta: authentication('menu.login', 'login'),
        component: () => import('@/pages/auth/Login.vue'),
      },
      {
        name: 'sign-up',
        path: 'sign-up',
        meta: authentication('auth.signup_title', 'sign-up'),
        component: () => import('@/pages/auth/Signup.vue'),
      },
      {
        name: 'recover-password',
        path: 'recover-password',
        meta: authentication('menu.recover_password', 'recover-password'),
        component: () => import('@/pages/auth/RecoverPassword.vue'),
      },
      {
        name: 'reset-password',
        path: 'reset-password/:uidb64/:token',
        meta: authentication('login.reset_password', 'reset-password'),
        component: () => import('@/pages/auth/ResetPassword.vue'),
      },
    ],
  },
  {
    name: '404',
    path: '/404',
    meta: { title: 'interface.page_not_found', pageFamily: 'system' satisfies DashboardPageFamily },
    component: () => import('@/dashboard/themes/theme-6/NotFound.vue'),
  },
  { path: '/:pathMatch(.*)*', redirect: () => ({ name: '404' }) },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior(to, _from, savedPosition) {
    if (savedPosition) return savedPosition
    if (to.hash) return { el: to.hash, behavior: 'smooth' }
    return { top: 0 }
  },
})

export function routeCapability(to: RouteLocationNormalized): Capability | undefined {
  return to.matched.map((record) => record.meta.capability).find(Boolean)
}

router.beforeEach(async (to) => {
  document.title = to.meta.title ? `${i18n.global.t(to.meta.title)} · ${clientConfig.name}` : clientConfig.name
  if (!to.matched.some((record) => record.meta.requiresAuth)) return true

  const auth = useAuthStore()
  if (!auth.ready) auth.boot()
  if (!auth.accessToken) return { name: 'login', query: { redirect: to.name?.toString() } }

  if (!auth.profileSynchronized) {
    try {
      await auth.fetchProfile()
    } catch {
      auth.logout()
      return { name: 'login', query: { redirect: to.name?.toString() } }
    }
  }

  const capability = routeCapability(to)
  if (capability && !hasCapability(auth.profile, capability)) {
    return { name: auth.profile?.is_marketer ? 'marketer' : 'home' }
  }
  return true
})

export default router
